import React, { useEffect, useState } from "react";
import { Modal, Input, Switch, Upload, Button, Form, notification } from "antd";
import { LuX, LuUpload } from "react-icons/lu";
import { useUploadFileMutation } from "../../store/api/apiSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentRole, setCredentials } from "../../store/auth/authSlice";
import axios from "axios";
import { getEnvironmentConfig } from "../../config/environment";

const { baseURL } = getEnvironmentConfig();

export const SettingsModal = ({ visible, onClose }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const token = useSelector((s) => s.auth.token);
  const userDatas = useSelector((s) => s.auth);
  const userData = userDatas?.user?.data;
  const userRole = useSelector(selectCurrentRole);

  const [uploadFile] = useUploadFileMutation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileList, setFileList] = useState({
    termsDoc: [],
    termsVideo: [],
    privacyDoc: [],
    privacyVideo: [],
  });

  const resetSettingsForm = () => {
    form.resetFields();
    form.setFieldsValue({
      email: false,
      system: false,
    });
    setFileList({
      termsDoc: [],
      termsVideo: [],
      privacyDoc: [],
      privacyVideo: [],
    });
  };

  const applySettingsData = (settingsSource) => {
    if (!settingsSource || Object.keys(settingsSource).length === 0) {
      resetSettingsForm();
      return;
    }

    form.setFieldsValue({
      email: settingsSource?.notification?.email || false,
      system: settingsSource?.notification?.system || false,
    });

    setFileList({
      termsDoc: settingsSource?.terms_condition
        ? [
            {
              uid: "-1",
              name: "Terms & Conditions",
              status: "done",
              url: settingsSource.terms_condition,
            },
          ]
        : [],
      termsVideo: settingsSource?.terms_condition_video
        ? [
            {
              uid: "-2",
              name: "Terms & Conditions Video",
              status: "done",
              url: settingsSource.terms_condition_video,
            },
          ]
        : [],
      privacyDoc: settingsSource?.privacy_policy
        ? [
            {
              uid: "-1",
              name: "Privacy Policy",
              status: "done",
              url: settingsSource.privacy_policy,
            },
          ]
        : [],
      privacyVideo: settingsSource?.privacy_policy_video
        ? [
            {
              uid: "-3",
              name: "Privacy Policy Video",
              status: "done",
              url: settingsSource.privacy_policy_video,
            },
          ]
        : [],
    });
  };

  const uploadIfExists = async (fileItem, filename, errorMessage) => {
    if (!fileItem?.originFileObj) return "";

    try {
      const res = await uploadFile({
        file: fileItem.originFileObj,
        filename,
      }).unwrap();

      return res?.file || "";
    } catch {
      notification.error({
        message: "Upload Failed",
        description: errorMessage,
      });
      throw new Error(errorMessage);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const values = await form.validateFields();

      const termsFileUrl = await uploadIfExists(
        fileList.termsDoc[0],
        "termsConditions",
        "Failed to upload terms document",
      );

      const termsVideoUrl = await uploadIfExists(
        fileList.termsVideo[0],
        "termsConditionsVideo",
        "Failed to upload terms conditions video",
      );

      const privacyFileUrl = await uploadIfExists(
        fileList.privacyDoc[0],
        "privacyStatement",
        "Failed to upload privacy policy",
      );

      const privacyVideoUrl = await uploadIfExists(
        fileList.privacyVideo[0],
        "privacyStatementVideo",
        "Failed to upload privacy policy video",
      );

      const settingsData = {
        notification: {
          email: values.email,
          system: values.system,
        },
        type: userData?.type || "admin",
        ...(termsFileUrl && { terms_condition: termsFileUrl }),
        ...(termsVideoUrl && { terms_condition_video: termsVideoUrl }),
        ...(privacyFileUrl && { privacy_policy: privacyFileUrl }),
        ...(privacyVideoUrl && { privacy_policy_video: privacyVideoUrl }),
      };

      const rolePath = userRole === "admin" ? "admin" : "user";

      const response = await axios.put(`${baseURL}/${rolePath}/profile`, settingsData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.success) {
        notification.success({
          message: "Success",
          description: "Settings updated successfully",
        });

        dispatch(
          setCredentials({
            ...userDatas,
            user: {
              ...userDatas.user,
              data: {
                ...userDatas.user.data,
                notification: settingsData.notification,
                terms_condition: termsFileUrl || userDatas?.user?.data?.terms_condition || null,
                terms_condition_video:
                  termsVideoUrl || userDatas?.user?.data?.terms_condition_video || null,
                privacy_policy: privacyFileUrl || userDatas?.user?.data?.privacy_policy || null,
                privacy_policy_video:
                  privacyVideoUrl || userDatas?.user?.data?.privacy_policy_video || null,
              },
            },
          }),
        );

        onClose();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!visible) return;

    let isMounted = true;

    const fetchUserSettings = async () => {
      try {
        const rolePath = userRole === "admin" ? "admin" : "user";
        const response = await axios.get(`${baseURL}/${rolePath}/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!isMounted) return;

        const settingsFromApi =
          response?.data?.data || response?.data?.user || response?.data || null;
        applySettingsData(settingsFromApi);
      } catch (error) {
        if (!isMounted) return;

        if (userData) {
          applySettingsData(userData);
        } else {
          resetSettingsForm();
        }
      }
    };

    fetchUserSettings();

    return () => {
      isMounted = false;
    };
  }, [visible, userRole, token]);

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={500}
      centered
      closeIcon={
        <div className="bg-secondary h-8 w-8 flex justify-center items-center rounded-full relative top-1">
          <LuX className="text-red-400" size={20} />
        </div>
      }
      title={<h3 className="text-xl font-semibold relative -top-5 mb-0">Settings</h3>}
      className="[&_.ant-modal]:overflow-hidden [&_.ant-modal-content]:overflow-hidden"
    >
      <Form form={form} onFinish={handleSubmit} className="flex max-h-[calc(100vh-180px)] flex-col">
        <div className="flex-1 overflow-y-auto pr-1">
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-3 mt-1">
              <span className="text-sm sm:text-base">Email Notifications</span>
              <Form.Item name="email" valuePropName="checked" className="mb-0">
                <Switch size="large" />
              </Form.Item>
            </div>
            <div className="flex items-center justify-between gap-3 mt-1">
              <span className="text-sm sm:text-base">System Notifications</span>
              <Form.Item name="system" valuePropName="checked" className="mb-0">
                <Switch size="large" />
              </Form.Item>
            </div>
            {userRole === "admin" && (
              <div className="space-y-4 pt-3">
                <div className="pt-3">
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Terms & Conditions
                  </label>
                  <Upload
                    accept=".pdf,.doc,.docx"
                    fileList={fileList.termsDoc}
                    onChange={({ fileList: nextFileList }) =>
                      setFileList((p) => ({ ...p, termsDoc: nextFileList }))
                    }
                    maxCount={1}
                    customRequest={({ onSuccess }) => onSuccess("ok")}
                  >
                    <Input
                      placeholder="Upload Terms & Conditions PDF"
                      className="h-11 rounded-2xl bg-white cursor-pointer"
                      readOnly
                    />
                  </Upload>
                </div>
                <div className="pt-3">
                  <Upload
                    accept=".mp4"
                    fileList={fileList.termsVideo}
                    onChange={({ fileList: nextFileList }) =>
                      setFileList((p) => ({ ...p, termsVideo: nextFileList }))
                    }
                    maxCount={1}
                    customRequest={({ onSuccess }) => onSuccess("ok")}
                  >
                    <Input
                      placeholder="Upload Terms & Conditions Video"
                      className="h-11 rounded-2xl bg-white cursor-pointer"
                      suffix={<LuUpload />}
                      readOnly
                    />
                  </Upload>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Privacy Policy
                  </label>
                  <Upload
                    accept=".pdf,.doc,.docx"
                    fileList={fileList.privacyDoc}
                    onChange={({ fileList: nextFileList }) =>
                      setFileList((p) => ({ ...p, privacyDoc: nextFileList }))
                    }
                    maxCount={1}
                    customRequest={({ onSuccess }) => onSuccess("ok")}
                  >
                    <Input
                      placeholder="Upload PDF..."
                      className="h-11 rounded-2xl bg-white cursor-pointer"
                      readOnly
                    />
                  </Upload>
                </div>
                <div>
                  <Upload
                    accept=".mp4"
                    fileList={fileList.privacyVideo}
                    onChange={({ fileList: nextFileList }) =>
                      setFileList((p) => ({ ...p, privacyVideo: nextFileList }))
                    }
                    maxCount={1}
                    customRequest={({ onSuccess }) => onSuccess("ok")}
                  >
                    <Input
                      placeholder="Upload video file..."
                      className="h-11 rounded-2xl bg-white cursor-pointer"
                      readOnly
                      suffix={<LuUpload />}
                    />
                  </Upload>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row justify-between pt-4 gap-3 items-center shrink-0">
            <Button
              onClick={onClose}
              className="w-full sm:w-[59%] rounded-2xl flex items-center justify-center gap-2"
              style={{ borderRadius: "16px" }}
            >
              Cancel
            </Button>
            <Button
              htmlType="submit"
              loading={isSubmitting}
              className="w-full sm:w-[59%] bg-[#202835] text-white rounded-2xl flex items-center justify-center gap-2"
              style={{ borderRadius: "16px" }}
            >
              Save Changes
            </Button>
        </div>
      </Form>
    </Modal>
  );
};
